import React, { useMemo, useState } from "react";
import { EmptyState, SkeletonRows } from "./Ui";
import SourceTip from "./SourceTip";

/**
 * Lightweight sortable table.
 *
 * columns: [{ key, label, render?(row), sortValue?(row), align?: "left"|"right"|"center", width?, className?, hideBelow?: "md",
 *             metric?: string  // data-source key -> shows a SourceTip in the header }]
 * rows: array of objects. rowKey: fn(row) -> string.
 */
export default function DataTable({
  columns,
  rows,
  rowKey,
  loading,
  onRowClick,
  defaultSort,
  pageSize = 0,
  emptyTitle = "No rows",
  emptyMessage,
  dense = false,
  stickyHeader = true,
  footer,
}) {
  const [sort, setSort] = useState(defaultSort || null); // { key, dir: "asc"|"desc" }
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!rows) return [];
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const getter = col.sortValue || ((r) => r[col.key]);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va === vb) return 0;
      if (va === null || va === undefined || va === "") return 1;
      if (vb === null || vb === undefined || vb === "") return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
  }, [rows, sort, columns]);

  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const current = Math.min(page, pages);
  const visible = pageSize ? sorted.slice((current - 1) * pageSize, current * pageSize) : sorted;

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setPage(1);
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: col.defaultDir || "desc" };
      return { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };

  return (
    <div className={`bd-table-wrap ${dense ? "bd-table-wrap--dense" : ""}`}>
      <table className={`bd-table ${stickyHeader ? "bd-table--sticky" : ""}`}>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort && sort.key === c.key;
              return (
                <th
                  key={c.key}
                  style={{ width: c.width, textAlign: c.align || "left" }}
                  className={`${c.sortable === false ? "" : "is-sortable"} ${active ? "is-sorted" : ""} ${c.className || ""} ${c.hideBelow ? `hide-below-${c.hideBelow}` : ""}`}
                  onClick={() => toggleSort(c)}
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <span>
                    {c.label}
                    {c.metric && <SourceTip metric={c.metric} title={typeof c.label === "string" ? c.label : undefined} align={c.align === "right" ? "right" : "left"} />}
                    {c.sortable !== false && <i className={`bd-table__sort ${active ? sort.dir : ""}`} />}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length}>
                <SkeletonRows rows={6} />
              </td>
            </tr>
          ) : !visible.length ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState title={emptyTitle} message={emptyMessage} />
              </td>
            </tr>
          ) : (
            visible.map((row, i) => (
              <tr
                key={rowKey ? rowKey(row) : i}
                className={onRowClick ? "is-clickable" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => (e.key === "Enter" ? onRowClick(row) : null) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align || "left" }} className={`${c.className || ""} ${c.hideBelow ? `hide-below-${c.hideBelow}` : ""}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {footer && (
          <tfoot>
            <tr>{footer}</tr>
          </tfoot>
        )}
      </table>
      {pageSize > 0 && pages > 1 && (
        <div className="bd-table-pagination">
          <span>
            {(current - 1) * pageSize + 1}–{Math.min(current * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div>
            <button type="button" className="bd-btn bd-btn--ghost" disabled={current <= 1} onClick={() => setPage(current - 1)}>
              Prev
            </button>
            <button type="button" className="bd-btn bd-btn--ghost" disabled={current >= pages} onClick={() => setPage(current + 1)}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
